import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, combineLatest, firstValueFrom } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { CurrenciesInterface } from 'src/app/shared/interfaces/deposit.interface';
import { RateItem } from 'src/app/shared/interfaces/wallet.interface';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';

export type ConversionCurrency = {
  currency: CurrenciesInterface | null;
  enable: boolean;
  loading: boolean;
  rate: RateItem[];
};

export type DefaultFiatToFiat = {
  currency: CurrenciesInterface | null;
  rates: RateItem[] | null;
};

@Injectable({
  providedIn: 'root',
})
export class CurrencyWalletService {
  /** 默认币种 和 其他法币兑换 汇率*/
  defaultToFiat$: BehaviorSubject<DefaultFiatToFiat> = new BehaviorSubject<DefaultFiatToFiat>({
    currency: null,
    rates: [],
  });

  /** 当前显示用于换算的币种 */
  conversionCurrency$: BehaviorSubject<ConversionCurrency> = new BehaviorSubject<ConversionCurrency>({
    currency: null,
    enable: false,
    loading: false,
    rate: [],
  });

  constructor(
    private userAssetsService: UserAssetsService,
    private appService: AppService,
    private localStorageService: LocalStorageService,
  ) {}

  /**
   *
   * @param currencies
   * @param data
   * @returns
   */
  async updateRate() {
    const self = this.conversionCurrency$.value;
    if (!self || !self.currency || self.loading) return;
    this.conversionCurrency$.next({ ...self, loading: true });
    const baseCurrency = self.currency.currency;
    const res = this.userAssetsService.getRatesBaseCurrency(baseCurrency);
    this.conversionCurrency$.next({
      ...self,
      loading: false,
      rate: res.rates || [],
    });
  }

  async updateConversion(status: boolean, currency?: string) {
    this.localStorageService.conversionSwitch = status ? '1' : '0';
    if (currency) {
      this.localStorageService.conversionCurrency = currency;
      const currencies = await firstValueFrom(this.appService.currencies$.pipe(first(x => x.length > 0)));
      const currencyItem = currencies.find(x => x?.currency === currency) || currencies[0];
      const self = this.conversionCurrency$.value;
      this.conversionCurrency$.next({
        ...self,
        currency: currencyItem,
        enable: status,
      });
      this.updateRate();
    } else {
      const self = this.conversionCurrency$.value;
      this.conversionCurrency$.next({
        ...self,
        enable: status,
      });
    }
  }

  amountAfterCalculateRate(balance: number, currency: string) {
    const rate = this.conversionCurrency$.value.rate.find(x => x.currency === currency);
    if (rate) return Number(balance ?? 0).subtract(rate.rate);
    return 0;
  }

  getDefaultToFiatRates(currentCurrency?: CurrenciesInterface | null): RateItem[] {
    const defaultToFiat = this.defaultToFiat$.value;
    if (!currentCurrency?.currency || defaultToFiat.currency?.currency === currentCurrency.currency) {
      return defaultToFiat.rates || [];
    }

    return this.userAssetsService.getRatesBaseCurrency(currentCurrency.currency).rates || [];
  }

  /** 法币换算订阅 */
  conversion$!: Subscription;

  /**初始化法币换算显示 */
  initConversion() {
    this.conversion$?.unsubscribe();
    this.conversion$ = combineLatest([
      this.appService.currencies$.pipe(map(v => v.filter(x => !x.isDigital))),
      this.appService.currentCurrency$,
    ]).subscribe(data => {
      const conversionCurrency = this.localStorageService.conversionCurrency;
      const status = !!Number(this.localStorageService.conversionSwitch);
      if (data) {
        const currentCurrency = data[1]?.currency || '';
        const fiats = data[0] ?? [];
        if (
          fiats.find(currency => currency?.currency === conversionCurrency) ||
          fiats.find(currency => currency?.currency === currentCurrency)
        ) {
          this.updateConversion(status, conversionCurrency ? conversionCurrency : currentCurrency);
        } else {
          this.updateConversion(false);
        }
      }
    });
  }
}
